import React, {FC, useEffect, useMemo} from 'react';
import classNames from 'classnames';
import {Button} from 'components';
import {statesAbbreviation} from 'definitions/States';
import AudioListSkeleton from 'features/Provider/Experiences/WellniteAudios/Library/AudioListSkeleton';
import {DirectoryAudioItem} from 'features/Provider/Experiences/WellniteAudios/Library/DirectoryAudioItem';
import {ProviderArticleList} from 'features/Provider/Marketing/Public/ProviderArticleList';
import {
  useBreakpoint,
  useLocale,
  useSetPreferredLanguage,
  useToggle,
  useWellniteAudios,
} from 'hooks';
import {AppointmentDurations, AppointmentTypes, UserRoles} from 'interfaces';
import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useParams} from 'react-router';
import {getCountryNameFromLocale, isMobile as isMobileDevice} from 'utils';

import {DirectBookingHeader} from '../DirectBookingHeader';
import {
  getDirectBookingSelectedProvider,
  selectProvider,
} from '../directBookingSelectors';
import {directBookingActions} from '../directBookingSlice';
import {useDirectBookingFilter} from '../useDirectBookingFilter';

import {DirectBookingAvailability} from './DirectBookingAvailability';
import DirectBookingProviderProfile from './DirectBookingProviderProfile';
import {DirectBookingBookSessionSkeleton} from './DirectBookingProviderSkeleton';
import {DirectBookingSubmitButton} from './DirectBookingSubmitButton';
import {useDirectBookingAvailability} from './useDirectBookingAvailability';

const DirectBookingProvider: FC = () => {
  useSetPreferredLanguage();
  const {userId} = useParams<{userId: string}>();
  const selectedProvider = useSelector(getDirectBookingSelectedProvider);
  const providerById = useSelector(selectProvider(userId));
  const provider = selectedProvider ?? providerById;
  const filter = useDirectBookingFilter(provider);
  const {audios, handlePlay, currentlyPlayingId, isLoadingAudios} =
    useWellniteAudios(userId);

  const dispatch = useDispatch();
  const {
    toggleOn: showAvailabilityModal,
    onToggleClick: toggleAvailabilityModal,
  } = useToggle();
  const {isMd: isMediumScreen} = useBreakpoint('md');
  const isMobile = !isMediumScreen || isMobileDevice;

  const profession =
    filter.selectedOptions?.profession || provider?.profession || undefined;
  const role =
    profession &&
    (profession.toLowerCase() === UserRoles.therapist
      ? UserRoles.therapist
      : UserRoles.prescriber);

  const appointmentType =
    role === UserRoles.therapist
      ? AppointmentTypes.video_call_with_therapist
      : AppointmentTypes.doctor_consultation;
  const duration =
    role === UserRoles.therapist
      ? AppointmentDurations.thirty
      : AppointmentDurations.ten;

  const state = useMemo(
    () =>
      statesAbbreviation
        .find(
          s =>
            s.value.toLowerCase() ===
            filter.selectedOptions?.state?.toLowerCase(),
        )
        ?.label.toLowerCase() ?? '',
    [filter.selectedOptions?.state],
  );

  const availability = useDirectBookingAvailability(
    appointmentType,
    duration,
    provider.providerId,
    role as UserRoles.prescriber | UserRoles.therapist,
    state, // this is required for doctor consultation
    provider.calendarId,
  );

  useEffect(() => {
    if (!Object.keys(provider).length) {
      dispatch(directBookingActions.getProviderById(userId));
    }
  }, [provider]);

  const {locale} = useLocale();
  const providerCountry =
    provider?.country ?? (provider?.state?.length > 0 ? 'United States' : '');
  const currentLocaleCountry = getCountryNameFromLocale(locale);
  const history = useHistory();

  useEffect(() => {
    if (
      Object.keys(provider).length &&
      providerCountry &&
      providerCountry !== currentLocaleCountry
    ) {
      history.push('/direct-booking/providers');
    }
  }, [provider]);

  const {t} = useTranslation();

  const {hash} = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.replace('#', '');

    const timeout = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({behavior: 'smooth'});
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>{t('title.getStarted', 'Get Started | Wellnite Health')}</title>
      </Helmet>
      <section className="w-screen text-center">
        <DirectBookingHeader />

        <section className="max-w-[1100px] mx-auto p-5 pt-10 grid xs:grid-cols-12 overflow-hidden gap-x-10 lg:gap-x-19 font-inter">
          <section
            className={classNames('xs:col-span-12 block', {
              'md:col-span-6 lg:col-span-7 ': !isMobile,
            })}
          >
            <DirectBookingProviderProfile provider={provider} />
          </section>

          {(!isMobile || showAvailabilityModal) && provider?.providerId ? (
            <section className="xs:col-span-12 lg:block md:col-span-6 lg:col-span-5">
              <DirectBookingAvailability
                onCloseClick={toggleAvailabilityModal}
                isModal={isMobile}
                filter={filter}
                availability={availability}
              />
            </section>
          ) : (
            <DirectBookingBookSessionSkeleton />
          )}
        </section>

        <section className="max-w-[1100px] mx-auto p-5 pb-7 -mt-20">
          <ProviderArticleList providerId={provider.userId} />
        </section>

        <section
          className="max-w-[1100px]  w-full  p-5 pb-32 md:pb-20 mx-auto"
          id="audios"
        >
          <h2 className="text-md py-4 font-semibold text-left">Audios</h2>
          {isLoadingAudios ? (
            <AudioListSkeleton />
          ) : (
            <div className=" flex flex-col gap-y-5">
              {audios?.map(audio => (
                <DirectoryAudioItem
                  key={audio._id}
                  {...audio}
                  isPlaying={currentlyPlayingId === audio._id}
                  onPlay={handlePlay}
                />
              ))}
            </div>
          )}
        </section>
      </section>

      {isMobile ? (
        <div className="fixed w-full bg-white border-t-2 bottom-0 left-0 z-10">
          <div className="w-full py-6">
            {!showAvailabilityModal ? (
              <Button
                type="button"
                onClick={toggleAvailabilityModal}
                borderColor="transparent"
                fontWeight="medium"
                className="block rounded-[11px] mx-auto px-8 py-3"
              >
                Book Session
              </Button>
            ) : (
              <DirectBookingSubmitButton
                filter={filter}
                availability={availability}
                providerId={provider.providerId}
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};
export default DirectBookingProvider;
